/**
 * SidebarAgentChats — the non-admin sidebar's agent list.
 *
 * Members don't administer agents, they *talk* to them, so the list reads like a
 * messaging app rather than a nav tree: one row per agent, a larger avatar, the
 * agent's name, and beneath it the title of their most recent conversation.
 *
 * Differences from the admin sidebar on purpose:
 * - every agent is listed (no "Show more" popover) and the list scrolls;
 * - there is no separate chat-history section — the latest chat is surfaced on
 *   the agent's own row instead, which is where a member looks for it.
 */

import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
} from '@/components/ui/sidebar'
import { AgentIcon } from '@/components/agents/agent-icon'
import { useChatHistory } from '@/hooks/use-chat-history'
import { cn } from '@/lib/utils'
import type { ChatHistoryApiItem } from '@taqat/core'

interface AgentSummary {
  id: string
  name: string
  avatar?: string | null
}

/** Most recent activity on a chat: `updatedAt` when present, else `createdAt`. */
function chatTimestamp(chat: ChatHistoryApiItem): number {
  const raw = chat.updatedAt || chat.createdAt
  const ms = raw ? new Date(raw).getTime() : 0
  return Number.isNaN(ms) ? 0 : ms
}

export function SidebarAgentChats({
  agents,
  appId,
}: {
  agents: AgentSummary[]
  appId?: string
}) {
  const location = useLocation()

  // Reuses the same history endpoint the admin sidebar lists chats from — we
  // just fold it down to "latest chat per agent" instead of rendering it as its
  // own section.
  const { state } = useChatHistory({ limit: 50, autoFetch: true })

  /** agentId -> that agent's most recent chat. */
  const latestByAgent = useMemo(() => {
    const map = new Map<string, ChatHistoryApiItem>()
    for (const chat of state.conversations) {
      if (!chat.agentId) continue
      const existing = map.get(chat.agentId)
      if (!existing || chatTimestamp(chat) > chatTimestamp(existing)) {
        map.set(chat.agentId, chat)
      }
    }
    return map
  }, [state.conversations])

  /**
   * Most recently used agents first, like a messaging app; agents you have
   * never spoken to fall to the bottom (keeping their original order among
   * themselves). Without this the untouched agents float to the top and the
   * live conversations sink out of view.
   */
  const ordered = useMemo(() => {
    return [...agents].sort((a, b) => {
      const aTime = latestByAgent.get(a.id) ? chatTimestamp(latestByAgent.get(a.id)!) : 0
      const bTime = latestByAgent.get(b.id) ? chatTimestamp(latestByAgent.get(b.id)!) : 0
      return bTime - aTime
    })
  }, [agents, latestByAgent])

  if (agents.length === 0) return null

  const basePath = `/app/${appId || ''}`

  return (
    <SidebarGroup className="min-h-0 flex-1 py-1">
      <SidebarGroupLabel className="flex w-full items-center justify-between px-3 text-muted-foreground group-data-[collapsible=icon]:hidden">
        <span>Agents</span>
        <span className="type-sidebar-meta text-muted-foreground/70">{agents.length}</span>
      </SidebarGroupLabel>

      {/* Every agent is listed rather than collapsing behind a "Show more": a
          member's agents are their whole workspace. This is the rail's single
          scroll area — the task peek above stays pinned, so scrolling a long
          agent list never pushes a member's recent tasks out of reach. */}
      <SidebarGroupContent className="min-h-0 flex-1 overflow-y-auto">
        <ul className="flex flex-col gap-0.5 px-2" data-testid="sidebar-agent-chats">
          {ordered.map((agent) => {
            const latest = latestByAgent.get(agent.id)
            // Land on the most recent conversation when there is one — that is
            // what tapping a chat does — otherwise open a fresh one.
            const target = latest
              ? `${basePath}/agents/${agent.id}/chat/${latest.id}`
              : `${basePath}/agents/${agent.id}`
            const active =
              location.pathname === target ||
              location.pathname.startsWith(`${basePath}/agents/${agent.id}`)

            return (
              <li key={agent.id}>
                <Link
                  to={target}
                  data-testid="sidebar-agent-chat-row"
                  data-agent-id={agent.id}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors',
                    'hover:bg-sidebar-accent',
                    active && 'bg-sidebar-accent',
                  )}
                >
                  <AgentIcon
                    avatar={agent.avatar ?? undefined}
                    name={agent.name}
                    size="md"
                    rounded="full"
                    className="size-9 shrink-0"
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium text-sidebar-foreground">
                      {agent.name}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {latest?.title || 'No conversations yet'}
                    </span>
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
